/* Cajita — SVG charts (portados 1:1 del prototipo claude.ai/design) */
import { useId, useState } from 'react';

function smoothPath(pts: [number, number][]): string {
  if (pts.length < 2) return '';
  let d = `M ${pts[0]![0]},${pts[0]![1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const [x0, y0] = pts[i]!;
    const [x1, y1] = pts[i + 1]!;
    const cx = (x0 + x1) / 2;
    d += ` C ${cx},${y0} ${cx},${y1} ${x1},${y1}`;
  }
  return d;
}

export interface LinePoint {
  m?: string;
  d?: string;
  [k: string]: number | string | undefined;
}

export function LineChart({
  data,
  keys,
  colors,
  height = 200,
  fmtY,
  smooth = true,
}: {
  data: LinePoint[];
  keys: string[];
  colors: string[];
  height?: number;
  fmtY?: (v: number) => string;
  smooth?: boolean;
}): JSX.Element {
  const W = 640;
  const H = height;
  const pad = { t: 16, r: 16, b: 28, l: 44 };
  const iw = W - pad.l - pad.r;
  const ih = H - pad.t - pad.b;
  const allVals = data.flatMap((d) => keys.map((k) => Number(d[k] ?? 0)));
  const max = Math.max(...allVals, 1) * 1.12;
  const min = 0;
  const x = (i: number) => pad.l + (i / (data.length - 1)) * iw;
  const y = (v: number) => pad.t + ih - ((v - min) / (max - min)) * ih;
  const ticks = 4;
  const [hover, setHover] = useState<number | null>(null);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height={height}
      style={{ overflow: 'visible' }}
      onMouseLeave={() => setHover(null)}
    >
      {Array.from({ length: ticks + 1 }).map((_, i) => {
        const v = (max / ticks) * i;
        const yy = y(v);
        return (
          <g key={i}>
            <line x1={pad.l} y1={yy} x2={W - pad.r} y2={yy} stroke="#EEF2F7" strokeWidth="1" />
            <text x={pad.l - 8} y={yy + 4} textAnchor="end" fontSize="11" fill="#94A3B8" fontWeight="500">
              {fmtY ? fmtY(v) : v}
            </text>
          </g>
        );
      })}
      {keys.map((k, ki) => {
        const pts = data.map((d, i) => [x(i), y(Number(d[k] ?? 0))] as [number, number]);
        const path = smooth ? smoothPath(pts) : 'M ' + pts.map((p) => p.join(',')).join(' L ');
        const area = path + ` L ${x(data.length - 1)},${pad.t + ih} L ${pad.l},${pad.t + ih} Z`;
        return (
          <g key={k}>
            <defs>
              <linearGradient id={`cxg-${k}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors[ki]} stopOpacity="0.18" />
                <stop offset="100%" stopColor={colors[ki]} stopOpacity="0" />
              </linearGradient>
            </defs>
            {ki === 0 && <path d={area} fill={`url(#cxg-${k})`} />}
            <path d={path} fill="none" stroke={colors[ki]} strokeWidth="2.5" strokeLinecap="round" />
          </g>
        );
      })}
      {data.map((_, i) => (
        <rect
          key={i}
          x={x(i) - iw / data.length / 2}
          y={pad.t}
          width={iw / data.length}
          height={ih}
          fill="transparent"
          onMouseEnter={() => setHover(i)}
        />
      ))}
      {hover != null && (
        <g>
          <line
            x1={x(hover)}
            y1={pad.t}
            x2={x(hover)}
            y2={pad.t + ih}
            stroke="#CBD5E1"
            strokeWidth="1"
            strokeDasharray="3 3"
          />
          {keys.map((k, ki) => (
            <circle key={k} cx={x(hover)} cy={y(Number(data[hover]![k] ?? 0))} r="4.5" fill="#fff" stroke={colors[ki]} strokeWidth="2.5" />
          ))}
        </g>
      )}
      {data.map((d, i) => (
        <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize="11" fill="#94A3B8" fontWeight="500">
          {d.m || d.d}
        </text>
      ))}
      {hover != null &&
        (() => {
          const bx = Math.min(Math.max(x(hover) - 60, 4), W - 124);
          return (
            <g transform={`translate(${bx}, ${pad.t})`}>
              <rect width="120" height={18 + keys.length * 16} rx="8" fill="#111827" />
              <text x="10" y="14" fontSize="10" fill="#9CA3AF" fontWeight="600">
                {data[hover]!.m || data[hover]!.d}
              </text>
              {keys.map((k, ki) => (
                <g key={k} transform={`translate(10, ${28 + ki * 16})`}>
                  <circle cx="3" cy="-3" r="3" fill={colors[ki]} />
                  <text x="12" y="0" fontSize="11" fill="#fff" fontWeight="600">
                    {fmtY ? fmtY(Number(data[hover]![k] ?? 0)) : data[hover]![k]}
                  </text>
                </g>
              ))}
            </g>
          );
        })()}
    </svg>
  );
}

export function BarChart({
  data,
  height = 200,
  fmtY,
  accentIdx,
}: {
  data: { d: string; v: number }[];
  height?: number;
  fmtY?: (v: number) => string;
  accentIdx?: number;
}): JSX.Element {
  const W = 560;
  const H = height;
  const pad = { t: 16, r: 8, b: 28, l: 44 };
  const iw = W - pad.l - pad.r;
  const ih = H - pad.t - pad.b;
  const max = Math.max(...data.map((d) => d.v), 1) * 1.15;
  const bw = (iw / data.length) * 0.56;
  const [hover, setHover] = useState<number | null>(null);
  const x = (i: number) => pad.l + (i + 0.5) * (iw / data.length);
  const y = (v: number) => pad.t + ih - (v / max) * ih;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={height} onMouseLeave={() => setHover(null)}>
      {[0, 1, 2, 3].map((i) => {
        const v = (max / 3) * i;
        const yy = y(v);
        return (
          <g key={i}>
            <line x1={pad.l} y1={yy} x2={W - pad.r} y2={yy} stroke="#EEF2F7" />
            <text x={pad.l - 8} y={yy + 4} textAnchor="end" fontSize="11" fill="#94A3B8" fontWeight="500">
              {fmtY ? fmtY(v) : v}
            </text>
          </g>
        );
      })}
      {data.map((d, i) => {
        const h = (d.v / max) * ih;
        const isHi = hover === i || accentIdx === i;
        return (
          <g key={i} onMouseEnter={() => setHover(i)}>
            <rect x={x(i) - bw / 2} y={pad.t} width={bw} height={ih} fill="transparent" />
            <rect
              x={x(i) - bw / 2}
              y={y(d.v)}
              width={bw}
              height={h}
              rx="6"
              fill={isHi ? '#5CC9EC' : '#BEFEFE'}
              style={{ transition: 'fill .15s' }}
            />
            <text
              x={x(i)}
              y={H - 8}
              textAnchor="middle"
              fontSize="11"
              fill={isHi ? '#475569' : '#94A3B8'}
              fontWeight={isHi ? 700 : 500}
            >
              {d.d}
            </text>
            {hover === i && (
              <g transform={`translate(${x(i)}, ${y(d.v) - 12})`}>
                <rect x="-32" y="-22" width="64" height="22" rx="6" fill="#111827" />
                <text x="0" y="-7" textAnchor="middle" fontSize="11" fill="#fff" fontWeight="600">
                  {fmtY ? fmtY(d.v) : d.v}
                </text>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
}

export function Donut({
  data,
  size = 180,
  thickness = 26,
  centerLabel,
  centerSub,
  fmtCenter,
}: {
  data: { label: string; value: number; color: string }[];
  size?: number;
  thickness?: number;
  centerLabel?: string;
  centerSub?: string;
  fmtCenter?: (v: number) => string;
}): JSX.Element {
  const total = data.reduce((a, d) => a + d.value, 0) || 1;
  const r = (size - thickness) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const C = 2 * Math.PI * r;
  let off = 0;
  const [hover, setHover] = useState<number | null>(null);
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#F1F5F9" strokeWidth={thickness} />
        {data.map((d, i) => {
          const frac = d.value / total;
          const len = frac * C;
          const el = (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={d.color}
              strokeWidth={hover === i ? thickness + 4 : thickness}
              strokeDasharray={`${len} ${C - len}`}
              strokeDashoffset={-off}
              strokeLinecap="butt"
              style={{ transition: 'stroke-width .15s', cursor: 'pointer' }}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            />
          );
          off += len;
          return el;
        })}
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeContent: 'center', textAlign: 'center' }}>
        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.02em' }} className="tnum">
          {hover != null ? (fmtCenter ? fmtCenter(data[hover]!.value) : data[hover]!.value) : centerLabel}
        </div>
        <div style={{ fontSize: 12, color: '#64748B', fontWeight: 500 }}>
          {hover != null ? data[hover]!.label : centerSub}
        </div>
      </div>
    </div>
  );
}

export function Gauge({
  value,
  max = 100,
  size = 200,
  label,
  sub,
  color = '#22C55E',
  track = '#E5E7EB',
}: {
  value: number;
  max?: number;
  size?: number;
  label?: string;
  sub?: string;
  color?: string;
  track?: string;
}): JSX.Element {
  const r = size / 2 - 16;
  const cx = size / 2;
  const cy = size / 2 + 8;
  const startA = Math.PI;
  const frac = Math.min(value / max, 1);
  const seg = 28;
  return (
    <div style={{ position: 'relative', width: size, height: size / 2 + 34 }}>
      <svg width={size} height={size / 2 + 34}>
        {Array.from({ length: seg }).map((_, i) => {
          const a = startA + (i / (seg - 1)) * Math.PI;
          const on = i / (seg - 1) <= frac;
          const ri = r + 2;
          const ro = r - 12;
          const x0 = cx + ro * Math.cos(a);
          const y0 = cy + ro * Math.sin(a);
          const x1 = cx + ri * Math.cos(a);
          const y1 = cy + ri * Math.sin(a);
          return (
            <line
              key={i}
              x1={x0}
              y1={y0}
              x2={x1}
              y2={y1}
              stroke={on ? color : track}
              strokeWidth="5"
              strokeLinecap="round"
              style={{ transition: 'stroke .3s' }}
            />
          );
        })}
      </svg>
      <div style={{ position: 'absolute', inset: 0, top: '26%', display: 'grid', placeContent: 'center', textAlign: 'center' }}>
        <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-.02em', color: '#0F172A' }} className="tnum">
          {label}
        </div>
        <div style={{ fontSize: 12, color: '#64748B', fontWeight: 500, marginTop: 2 }}>{sub}</div>
      </div>
    </div>
  );
}

export function Sparkline({
  data,
  color = '#84CC16',
  width = 110,
  height = 34,
  fill = true,
}: {
  data: number[];
  color?: string;
  width?: number;
  height?: number;
  fill?: boolean;
}): JSX.Element {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const last = data[data.length - 1] ?? 0;
  const x = (i: number) => (i / (data.length - 1)) * width;
  const y = (v: number) => height - 3 - ((v - min) / (max - min || 1)) * (height - 6);
  const pts = data.map((v, i) => [x(i), y(v)] as [number, number]);
  const path = smoothPath(pts);
  const id = useId().replace(/:/g, '');
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      {fill && (
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
      )}
      {fill && <path d={`${path} L ${width},${height} L 0,${height} Z`} fill={`url(#${id})`} />}
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={x(data.length - 1)} cy={y(last)} r="2.6" fill={color} />
    </svg>
  );
}

export function ProgressBar({
  value,
  color = '#22C55E',
  track = '#F1F5F9',
  height = 8,
}: {
  value: number;
  color?: string;
  track?: string;
  height?: number;
}): JSX.Element {
  return (
    <div style={{ background: track, borderRadius: 99, height, overflow: 'hidden', width: '100%' }}>
      <div
        style={{
          width: `${Math.min(value, 100)}%`,
          height: '100%',
          background: color,
          borderRadius: 99,
          transition: 'width .4s',
        }}
      />
    </div>
  );
}
